import { capDelay, exponentialBackoff } from 'retry-ts';

import type { task } from '@code-expert/prelude';
import { date } from '@code-expert/prelude';
import * as rpc from '@code-expert/rpc-client';
import type { ProcedureArgs, ProcedureName, ProcedureResult } from '@code-expert/rpc-common';
import type { CommandIndex, QueryIndex } from '/imports/api/rpc';
import { socket } from '/imports/startup/client/socket';

type Args<P> = [P] extends [undefined] ? [] : [P];

export const invokeProcedure: <N extends ProcedureName<CommandIndex>>(
  name: N,
) => (
  ...args: Args<ProcedureArgs<CommandIndex, N>>
) => task.Task<ProcedureResult<CommandIndex, N>> = rpc.invokeProcedure(socket) as $Inexpressible;

// We require this to be present in Playwright e2e tests.
(globalThis as $IntentionalAny).__PLAYWRIGHT_invokeProcedure__ = invokeProcedure;

export const useProcedure = rpc.makeUseProcedure<CommandIndex>(socket);

export const useCachedProcedure = rpc.makeUseCachedProcedure<CommandIndex>(socket);

// -------------------------------------------------------------------------------------------------

/**
 * We need to ensure to retry early in case the socket has not been connected yet
 * after startup. After this initial phase, retrying can slow down.
 */
const retryPolicy = capDelay(
  date.milliseconds({ seconds: 10 }),
  exponentialBackoff(date.milliseconds({ seconds: 0.2 })),
);

export const invokeQuery: <N extends ProcedureName<QueryIndex>>(
  name: N,
) => (...args: Args<ProcedureArgs<QueryIndex, N>>) => task.Task<ProcedureResult<QueryIndex, N>> =
  rpc.invokeProcedure(socket, {
    retryPolicy,
  }) as $Inexpressible;

export const useQuery = rpc.makeUseProcedure<QueryIndex>(socket, { retryPolicy });

export const useCachedQuery = rpc.makeUseCachedProcedure<QueryIndex>(socket, {
  retryPolicy,
});
